import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "./useStore";
import {
  fetchNotifications,
  fetchUnreadNotifications,
  fetchUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  setFilters,
  clearNotifications,
  clearError,
  resetUnreadCountCache,
} from "../store/slices/notificationSlice";
import type { NotificationFilters } from "../types/notifications";
import { useAuth } from "./useAuth";

export const useNotifications = () => {
  const dispatch = useAppDispatch();
  const {
    notifications,
    pagination,
    unreadCount,
    isUnreadCountLoaded,
    loading,
    error,
    filters,
  } = useAppSelector((state) => state.notifications);
  const { user } = useAuth();

  // Fetch notifications with filters
  const getNotifications = useCallback(
    (newFilters?: NotificationFilters) => {
      const filtersToUse = newFilters || filters;
      dispatch(fetchNotifications(filtersToUse));
    },
    [dispatch, filters]
  );

  // Fetch unread notifications only
  const getUnreadNotifications = useCallback(
    (params?: { page?: number; limit?: number }) => {
      dispatch(fetchUnreadNotifications(params));
    },
    [dispatch]
  );

  // Fetch unread count (with caching)
  const getUnreadCount = useCallback(() => {
    // Only fetch if not already loaded
    if (!isUnreadCountLoaded) {
      dispatch(fetchUnreadCount());
    }
  }, [dispatch, isUnreadCountLoaded]);

  // Force refresh unread count
  const refreshUnreadCount = useCallback(() => {
    dispatch(resetUnreadCountCache());
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  // Mark notification as read
  const markAsRead = useCallback(
    (notificationId: string) => {
      dispatch(markNotificationAsRead(notificationId));
    },
    [dispatch]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    dispatch(markAllNotificationsAsRead());
  }, [dispatch]);

  // Update filters
  const updateFilters = useCallback(
    (newFilters: Partial<NotificationFilters>) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch]
  );

  // Clear notifications
  const clearAllNotifications = useCallback(() => {
    dispatch(clearNotifications());
  }, [dispatch]);

  // Clear error
  const clearNotificationError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  /**
   * The bell's count, as soon as there is somebody to count for.
   *
   * `user?.role` is in the dependencies and the user object is not, deliberately
   * on both counts. It has to be there because the navbar can mount before the
   * signed-in user is readable, and an effect that only ran on mount would skip
   * the fetch and leave the bell blank until something else happened to
   * re-render it. It has to be the *role* rather than the object because
   * `useAuth` re-reads the user out of localStorage on every render and hands
   * back a fresh object each time — a dependency on that would re-run this on
   * every render of the navbar.
   *
   * Drivers are excluded because they have no bell: `/api/notifications` is
   * `authorize(["admin", "user"])`, so asking would be a guaranteed 403.
   */
  useEffect(() => {
    if (user && user.role !== "driver") {
      getUnreadCount();
    }
    // The rule wants the whole `user` object here; depending on it would re-run
    // this on every render, for the reason spelled out above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUnreadCount, user?.role]);

  return {
    // State
    notifications,
    pagination,
    unreadCount,
    isUnreadCountLoaded,
    loading,
    error,
    filters,

    // Actions
    getNotifications,
    getUnreadNotifications,
    getUnreadCount,
    refreshUnreadCount,
    markAsRead,
    markAllAsRead,
    updateFilters,
    clearAllNotifications,
    clearNotificationError,
  };
};
